import React, { useState, useMemo } from 'react';
import {
  Search, ChevronDown, ChevronRight, Plus, BarChart3,
  TrendingUp, PieChart, Activity, Hash, Layers, Filter,
} from 'lucide-react';
import { useReportsList } from '../../../reports/hooks/useReports';

const TYPE_ICON = {
  bar: BarChart3, line: TrendingUp, area: Activity,
  pie: PieChart, donut: PieChart, funnel: Filter,
  heatmap: Layers, kpi: Hash,
};

const TYPE_COLOR = {
  bar: 'bg-indigo-100 text-indigo-600',
  line: 'bg-blue-100 text-blue-600',
  area: 'bg-cyan-100 text-cyan-600',
  pie: 'bg-pink-100 text-pink-600',
  donut: 'bg-purple-100 text-purple-600',
  funnel: 'bg-amber-100 text-amber-600',
  heatmap: 'bg-emerald-100 text-emerald-600',
  kpi: 'bg-slate-100 text-slate-600',
};

const ReportPickerPanel = ({ onAddReport }) => {
  const [search, setSearch] = useState('');
  const [expandedFolders, setExpandedFolders] = useState(
    new Set(['Sales Reports', 'Marketing', 'Support', 'Executive', 'Unfiled']),
  );
  const { reports } = useReportsList();

  const grouped = useMemo(() => {
    const q = search.toLowerCase();
    const filtered = q
      ? reports.filter((r) => r.name.toLowerCase().includes(q) || r.folder?.toLowerCase().includes(q))
      : reports;
    const map = new Map();
    filtered.forEach((r) => {
      const folder = r.folder || 'Unfiled';
      if (!map.has(folder)) map.set(folder, []);
      map.get(folder).push(r);
    });
    return map;
  }, [reports, search]);

  const toggleFolder = (folder) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(folder)) next.delete(folder);
      else next.add(folder);
      return next;
    });
  };

  return (
    <div className="flex flex-col h-full bg-white border-r border-slate-200 select-none">
      <div className="px-4 pt-3 pb-2.5 border-b border-slate-100 flex-shrink-0 bg-gradient-to-r from-indigo-50/60 to-white">
        <div className="flex items-center gap-2 mb-2.5">
          <div className="w-5 h-5 rounded-md bg-indigo-100 flex items-center justify-center flex-shrink-0">
            <BarChart3 className="w-3 h-3 text-indigo-600" />
          </div>
          <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Add Reports</p>
        </div>
        <div className="relative">
          <Search className="absolute left-2.5 top-2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
          <input
            value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search reports…"
            className="w-full pl-8 pr-3 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white/80 focus:bg-white transition-colors"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {grouped.size === 0 && (
          <div className="flex flex-col items-center justify-center py-10 px-3 text-center">
            <Search className="w-6 h-6 text-slate-200 mb-2" />
            <p className="text-xs text-slate-400">No reports match your search</p>
          </div>
        )}
        {Array.from(grouped.entries()).map(([folder, reps]) => (
          <div key={folder}>
            <button
              className="w-full flex items-center gap-1.5 px-3 py-2 text-[10px] font-bold text-slate-400 uppercase tracking-widest hover:bg-slate-50 sticky top-0 bg-white z-10 border-b border-slate-50"
              onClick={() => toggleFolder(folder)}
            >
              {expandedFolders.has(folder)
                ? <ChevronDown className="w-3 h-3 flex-shrink-0 text-slate-300" />
                : <ChevronRight className="w-3 h-3 flex-shrink-0 text-slate-300" />}
              <span className="truncate">{folder}</span>
              <span className="ml-auto font-normal text-slate-300 tabular-nums">{reps.length}</span>
            </button>

            {expandedFolders.has(folder) && reps.map((r) => {
              const chartType = r.charts?.[0]?.type || 'bar';
              const Icon = TYPE_ICON[chartType] || BarChart3;
              const colorCls = TYPE_COLOR[chartType] || 'bg-indigo-100 text-indigo-600';
              return (
                <button
                  key={r.id}
                  onClick={() => onAddReport(r)}
                  className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-indigo-50 active:bg-indigo-100 group transition-colors"
                >
                  <div className={`w-7 h-7 flex-shrink-0 flex items-center justify-center rounded-lg ${colorCls}`}>
                    <Icon className="w-3.5 h-3.5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold text-slate-700 truncate group-hover:text-indigo-700 transition-colors">{r.name}</p>
                    <p className="text-[10px] text-slate-400 capitalize mt-0.5">{chartType} · {r.object}</p>
                  </div>
                  <div className="flex-shrink-0 w-5 h-5 rounded-full bg-indigo-100 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <Plus className="w-3 h-3 text-indigo-600" />
                  </div>
                </button>
              );
            })}
          </div>
        ))}
      </div>

      <div className="px-4 py-2.5 border-t border-slate-100 flex-shrink-0 bg-slate-50/60">
        <p className="text-[10px] text-slate-400 text-center leading-relaxed">
          Click a report to add it as a widget
        </p>
      </div>
    </div>
  );
};

export default ReportPickerPanel;
